import { Component, OnInit } from '@angular/core';
import { TabsService } from '../../../services/tabs.service';
import { UserService } from '../../../services/user.service';
import { UpdoxService } from '../../../services/updox.service';
import {
  UpdoxDirectMessage,
  ComposeDraft,
  MessageFolder
} from './updox-direct-message';
import { UpdoxContact } from './updox-contact';
import {
  ActiveView,
  FolderCount
} from './folder-list/folder-list.component';
import { GlobalErrorHandlerService } from '../../../services/global-error.service';

@Component({
  selector: 'app-updox-messaging',
  templateUrl: './updox-messaging.component.html',
})
export class UpdoxMessagingComponent implements OnInit {
  activeView: ActiveView = 'inbox';
  selectedMessage: UpdoxDirectMessage = null;
  composeDrafts: ComposeDraft[] = [];

  practiceId: number;
  updoxUserAddress: string = '';

  folderCounts: FolderCount = {
    inbox: 0,
    draft: 0,
    deleted: 0
  };

  private composeIdCounter = 0;
  triggerFolderReload = false;

  constructor(
    public tabs_service: TabsService,
    private userService: UserService,
    private updoxService: UpdoxService,
    private globalErrorHandler: GlobalErrorHandlerService,
  ) {}

  ngOnInit(): void {
    this.tabs_service.register('updox_messaging');

    const user = this.userService.current_user;

    // For practice/staff users, practices_id[0] is the current practice.
    // For enterprise users, profileable.practice_id holds it.
    this.practiceId =
      user.practices_id && user.practices_id.length > 0
        ? user.practices_id[0]
        : (user.profileable as any)?.practice_id ||
          user.profileable_id;

    this.updoxUserAddress =
      user.first_name.trim() + ' ' + user.last_name;

    // this.loadUpdoxUser();
    this.updoxService.loadUnreadCount(this.practiceId);
  }

  // Inbox badge reads the realtime unread count.
  // Draft and trash counts come from the loaded folder stats.
  get folderCountsView(): FolderCount {
    return {
      ...this.folderCounts,
      inbox: this.updoxService.unreadInboxCount
    };
  }

  /*
   * The message detail view is open whenever a message is selected.
   * This getter controls the "message-detail-open" class in the HTML.
   */
  get isMessageDetailOpen(): boolean {
    return !!this.selectedMessage;
  }

  private loadUpdoxUser(): void {
    if (!this.practiceId) {
      return;
    }

    this.updoxService
      .getUpdoxUser(this.practiceId)
      .then((resp: any[]) => {
        if (resp && resp.length > 0) {
          this.updoxUserAddress = resp[0].direct_address || '';
        }
      })
      .catch(() => {});
  }

  onFolderSelected(view: ActiveView): void {
    this.activeView = view;
    this.selectedMessage = null;
  }

  onCountsChanged(counts: {
    folder: MessageFolder;
    total: number;
    unread: number;
  }): void {
    if (counts.folder === 'draft') {
      this.folderCounts.draft = counts.total;
    }

    if (counts.folder === 'deleted') {
      this.folderCounts.deleted = counts.total;
    }
  }

  onMessageSelected(message: UpdoxDirectMessage): void {
    this.selectedMessage = message;
  }

  /*
   * Called when the user clicks Back/Close inside
   * app-updox-message-detail.
   */
  closeMessageDetail(): void {
    this.selectedMessage = null;
  }

  onMessageDeleted(message: UpdoxDirectMessage): void {
    this.selectedMessage = null;
    this.triggerFolderReload = !this.triggerFolderReload;
  }

  openCompose(prefill?: Partial<ComposeDraft>): void {
    const draft: ComposeDraft = {
      localId: ++this.composeIdCounter,
      draftId: prefill?.draftId,
      to_address: prefill?.to_address || '',
      subject: prefill?.subject || '',
      body_text: prefill?.body_text || '',
      patient_id: prefill?.patient_id,
      reply_to_message_id: prefill?.reply_to_message_id,
      minimized: false,
      replyTo: prefill?.replyTo,
    };

    this.composeDrafts.push(draft);
  }

  openComposeForContact(contact: UpdoxContact): void {
    this.openCompose({
      to_address: contact.direct_address
    });
  }

  openReply(draft: ComposeDraft): void {
    this.composeDrafts.push({
      ...draft,
      localId: ++this.composeIdCounter
    });
  }

  closeCompose(localId: number): void {
    this.composeDrafts = this.composeDrafts.filter(
      draft => draft.localId !== localId
    );
  }

  onEditDraft(message: UpdoxDirectMessage): void {
    this.openCompose({
      draftId: message.id,
      to_address: message.to_address,
      subject: message.subject,
      body_text: message.body_text,
      patient_id: message.patient_id,
    });
  }

  onMessageSent(message: UpdoxDirectMessage): void {
    // Reload sent or draft folder if active.
    if (
      this.activeView === 'sent' ||
      this.activeView === 'draft'
    ) {
      this.triggerFolderReload = !this.triggerFolderReload;
    }
  }

  onDraftSaved(message: UpdoxDirectMessage): void {
    // If viewing drafts, signal the message list to reload.
    if (this.activeView === 'draft') {
      this.triggerFolderReload = !this.triggerFolderReload;
    }
  }

  get isContactsView(): boolean {
    return this.activeView === 'contacts';
  }

  get activeFolder(): MessageFolder {
    return this.isContactsView
      ? 'inbox'
      : (this.activeView as MessageFolder);
  }
}